import React, { useState, useEffect } from "react";
import { toastr } from "react-redux-toastr";
import { connect } from "react-redux";
import { getDataFromAPI } from "../../../../HandleAPICalls/actions";
import { useParams, useNavigate } from "react-router-dom";
import debounce from 'lodash/debounce';
import CohortAssortmentCloneComponent from "./component";

const CohortAssortmentCloneContainer = (props) => {
  const { reference_id, assortmentId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState("");
  const [mvIdInput, setMvIdInput] = useState("");
  const [selectedMvIds, setSelectedMvIds] = useState([]);
  const [filteredMvList, setFilteredMvList] = useState([]);
  const [showMvIdDropdown, setShowMvIdDropdown] = useState(false);
  const [openUploadModal, setOpenUploadModal] = useState(false);
  const [mvIdsInput, setMvIdsInput] = useState("");
  const [isVerified, setIsVerified] = useState(false);
  const [activeTab, setActiveTab] = useState('list');
  const [categories, setCategories] = useState([]);
  const [verifiedMvIds, setVerifiedMvIds] = useState([]);
  const [invalidMvIds, setInvalidMvIds] = useState([]);
  const [duplicateMvIds, setDuplicateMvIds] = useState([]);
  const [alreadySelectedMvIds, setAlreadySelectedMvIds] = useState([]);
  const [assortmentDetails, setAssortmentDetails] = useState(null);
  const [getReferenceId, setGetReferenceId] = useState();
  const [ruleFailures, setRuleFailures] = useState([]);
  const [failureModalOpen, setFailureModalOpen] = useState(false);
  const [successModalOpen, setSuccessModalOpen] = useState(false);
  const [newAssortmentId, setNewAssortmentId] = useState(null);
  const [searchLoading, setSearchLoading] = useState(false); // New state

  useEffect(() => {
    fetchAssortmentDetails();
  }, []);

  const fetchAssortmentDetails = () => {
    setLoading(true);
    props.getDataFromAPI(
      `/partner/api/v1/assortment/${assortmentId}`,
      "GET",
      undefined,
      (response) => {
        setAssortmentDetails(response);
        setStartDate(response.start_date);
        setGetReferenceId(response.reference_id);
        const formattedItems = response.priority_wise_items.map(item => ({
          id: item.mv_id.toString(),
          variant_name: item.variant_name,
          mrp: item.offer_price,
          image_id: item.image
        }));

        setSelectedMvIds(formattedItems);
        generateCategories(formattedItems);
        setLoading(false);
      },
      (err) => {
        console.error("API Error:", err);
        setLoading(false);
        toastr.error("Failed to load assortment details", err.message);
      }
    );
  };

  const generateCategories = (items) => {
    const categoryMap = {};

    items.forEach(item => {
      const categoryName = item.variant_name?.includes('Biscuit') ? 'Biscuits' :
        item.variant_name?.includes('Drink') ? 'Beverages' : 'Others';

      if (!categoryMap[categoryName]) {
        categoryMap[categoryName] = {
          count: 0,
          items: [],
          image: item.image_id || null
        };
      }
      categoryMap[categoryName].items.push(item);
      categoryMap[categoryName].count++;
    });

    setCategories(Object.keys(categoryMap).map(categoryName => ({
      name: categoryName,
      count: categoryMap[categoryName].count,
      items: categoryMap[categoryName].items,
      image: categoryMap[categoryName].image
    })));
  };

  const handleCreateNewAssortment = async () => {
    if (!startDate) {
      toastr.error("Error", "Please select a start date");
      return;
    }

    if (selectedMvIds.length === 0) {
      toastr.error("Error", "Please add at least one MV ID");
      return;
    }

    const priorityItems = selectedMvIds
      .map(mv => ({ mv_id: parseInt(mv.id, 10) }))
      .filter(item => !isNaN(item.mv_id));

    const payload = {
      type: "cohort",
      reference_id: parseInt(getReferenceId),
      start_date: startDate,
      priority_wise_items: priorityItems,
    };

    try {
      const response = await new Promise((resolve, reject) => {
        props.getDataFromAPI(
          `/partner/api/v1/assortment/create`,
          "POST",
          payload,
          (successResponse) => resolve(successResponse),
          (error) => reject(error)
        );
      });

      if (response && response.id) {
        setNewAssortmentId(response.id);
        setSuccessModalOpen(true);
      } else if (response && Array.isArray(response)) {
        setRuleFailures(response);
        setFailureModalOpen(true);
      } else {
        toastr.error("Error", "Failed to create assortment");
      }
    } catch (err) {
      if (err.response && Array.isArray(err.response)) {
        setRuleFailures(err.response);
        setFailureModalOpen(true);
      } else {
        toastr.error("Error", err.message || "Failed to create assortment");
      }
    }
  };

  const handleOpenUploadModal = () => {
    setOpenUploadModal(true);
    setMvIdsInput("");
    setIsVerified(false);
    setDuplicateMvIds([]);
    setAlreadySelectedMvIds([]);
  };

  const handleCloseUploadModal = () => {
    setOpenUploadModal(false);
  };

  const handleDeleteMvId = (idToDelete) => {
    setSelectedMvIds(selectedMvIds.filter(item => item.id !== idToDelete.id));
    generateCategories(selectedMvIds.filter(item => item.id !== idToDelete.id));
  };

  // Enhanced MVID search functionality (same as in Rule Creation)
  const searchMvId = debounce((query = "") => {
    if (query.trim() === "") {
      setFilteredMvList([]);
      setShowMvIdDropdown(false);
      setSearchLoading(false);
      return;
    }

    setSearchLoading(true);
    setShowMvIdDropdown(true);

    props.getDataFromAPI(
      `/dashboard/api/v2/admin/mvs/all?limit=50&offset=0&isActive=1&search=${encodeURIComponent(query)}`,
      "GET",
      undefined,
      (response) => {
        const results = response?.list?.map(item => ({
          id: item.id,
          variant_name: item.variant_name,
          mrp: item.mrp,
          image_id: item.image_id || null,
          displayName: `${item.id} - ${item.variant_name || 'N/A'}`
        })) || [];
        
        setFilteredMvList(results);
        setSearchLoading(false);
      },
      (err) => {
        console.error("MVID search error:", err);
        setFilteredMvList([]);
        setSearchLoading(false);
      }
    );
  }, 300);

  const handleMvIdInputChange = (e) => {
    const value = e.target.value;
    // Allow only numbers for MV ID search
    if (/^\d*$/.test(value) || value === "") {
      setMvIdInput(value);
      searchMvId(value);
    }
  };

  const handleAddMvId = (mv) => {
    const idToAdd = mv?.id || mvIdInput.trim();
    if (!idToAdd) return;

    if (selectedMvIds.some(item => item.id === idToAdd.toString())) {
      toastr.warning("Warning", "This MV ID is already added");
      return;
    }

    const newItem = mv?.id ? mv : { 
      id: idToAdd, 
      variant_name: mv?.variant_name || "Unknown Product", 
      mrp: mv?.mrp || 0,
      image_id: mv?.image_id || null
    };
    
    const updatedSelectedMvIds = [...selectedMvIds, newItem];
    setSelectedMvIds(updatedSelectedMvIds);
    generateCategories(updatedSelectedMvIds);
    setMvIdInput("");
    setShowMvIdDropdown(false);
    setFilteredMvList([]);
  };

  // Rest of the functions remain the same...
  const handleBulkMvIdsPaste = (e) => {
    e.preventDefault();

    let pasteData = e.clipboardData.getData("Text");

    pasteData = pasteData
      .trim()
      .replace(/[\n\r\t]+/g, ",")
      .replace(/\s+/g, ",")
      .replace(/,+/g, ",");

    const cleaned = pasteData
      .split(",")
      .map((id) => id.replace(/^0+/, "").trim())
      .filter((id) => id.length > 0)
      .join(",");

    setMvIdsInput(cleaned);
  };

  const handleCheckMvIds = async () => {
    try {
      if (!mvIdsInput.trim()) {
        toastr.error("Error", "Please enter MV IDs to verify");
        return;
      }

      const inputIds = mvIdsInput.split(/[,\s]+/).filter(id => id !== '');
      
      const inputDuplicates = [];
      const uniqueInputIds = [];
      const seenIds = new Set();
      
      inputIds.forEach(id => {
        if (seenIds.has(id)) {
          inputDuplicates.push(id);
        } else {
          seenIds.add(id);
          uniqueInputIds.push(id);
        }
      });

      const selectedIds = selectedMvIds.map(item => item.id);
      const duplicatesWithSelected = uniqueInputIds.filter(id => 
        selectedIds.includes(id)
      );

      const allDuplicateIds = [...inputDuplicates, ...duplicatesWithSelected];
      setDuplicateMvIds(inputDuplicates);
      setAlreadySelectedMvIds(duplicatesWithSelected);

      const numericIds = uniqueInputIds
        .map(id => parseInt(id, 10))
        .filter(id => !isNaN(id));

      if (numericIds.length === 0) {
        toastr.error("Error", "No valid numeric MV IDs found");
        return;
      }

      const payload = { mvIds: numericIds };

      const response = await new Promise((resolve, reject) => {
        props.getDataFromAPI(
          `/partner/api/v1/assortment/items/check/all`,
          "POST", 
          payload,
          (successResponse) => resolve(successResponse),
          (error) => reject(error)
        );
      });

      if (response && response.mvPresent) {
        const validMvIds = [];
        const invalidMvIds = [...allDuplicateIds];
        
        response.mvPresent.forEach(item => {
          if (item.isPresent && !duplicatesWithSelected.includes(item.mvId.toString())) {
            const mvItem = {
              id: item.mvId.toString(),
              variant_name: item.name || 'Unknown Product',
              mrp: item.offerPrice || 0,
              image_id: item.image || null
            };
            validMvIds.push(mvItem);
          } else if (!duplicatesWithSelected.includes(item.mvId.toString())) {
            invalidMvIds.push(item.mvId.toString());
          }
        });

        setVerifiedMvIds(validMvIds);
        setInvalidMvIds(invalidMvIds);
        setIsVerified(true);
        
        if (inputDuplicates.length > 0 || duplicatesWithSelected.length > 0) {
          let message = '';
          if (inputDuplicates.length > 0) {
            message += `${inputDuplicates.length} duplicate MV IDs in your input. `;
          }
          if (duplicatesWithSelected.length > 0) {
            message += `${duplicatesWithSelected.length} MV IDs already exist in your selection. `;
          }
          message += 'These have been marked as invalid.';
          toastr.warning("Warning", message);
        }
        
        toastr.success(
          "Success", 
          `${validMvIds.length} valid MV IDs found, ${invalidMvIds.length} invalid`
        );
      } else {
        toastr.error("Error", "No valid MV IDs found");
      }
    } catch (err) {
      toastr.error("Error", "Failed to verify MV IDs");
    }
  };

  const handleRemoveInvalidMvId = (mvIdToRemove) => {
    const idToRemove = mvIdToRemove.toString();
    
    const updatedInvalidMvIds = invalidMvIds.filter(id => 
      id.toString() !== idToRemove
    );
    setInvalidMvIds(updatedInvalidMvIds);
    
    setDuplicateMvIds(duplicateMvIds.filter(id => id !== idToRemove));
    setAlreadySelectedMvIds(alreadySelectedMvIds.filter(id => id !== idToRemove));
    
    const updatedInput = mvIdsInput
      .split(',')
      .map(id => id.trim())
      .filter(id => id !== idToRemove)
      .join(', ');
      
    setMvIdsInput(updatedInput);
  };

  const handleContinue = () => {
    if (verifiedMvIds.length === 0) {
      toastr.error("Error", "No valid MV IDs to add");
      return;
    }

    const newItems = verifiedMvIds.filter(newItem =>
      !selectedMvIds.some(existingItem => existingItem.id === newItem.id)
    );

    if (newItems.length > 0) {
      const updatedSelectedMvIds = [...selectedMvIds, ...newItems];
      setSelectedMvIds(updatedSelectedMvIds);
      generateCategories(updatedSelectedMvIds);
      toastr.success("Success", `${newItems.length} MV IDs added successfully`);
    } else {
      toastr.info("Info", "No new MV IDs to add (all were duplicates)");
    }

    handleCloseUploadModal();
  };

  const handleResetVerification = () => {
    setIsVerified(false);
    setVerifiedMvIds([]);
    setInvalidMvIds([]);
    setDuplicateMvIds([]);
    setAlreadySelectedMvIds([]);
    setMvIdsInput("");
  };

  return (
    <CohortAssortmentCloneComponent
      startDate={startDate}
      setStartDate={setStartDate}
      mvIdInput={mvIdInput}
      handleMvIdInputChange={handleMvIdInputChange}
      selectedMvIds={selectedMvIds}
      handleAddMvId={handleAddMvId}
      handleDeleteMvId={handleDeleteMvId}
      filteredMvList={filteredMvList}
      showMvIdDropdown={showMvIdDropdown}
      setShowMvIdDropdown={setShowMvIdDropdown}
      handleCreateNewAssortment={handleCreateNewAssortment}
      openUploadModal={openUploadModal}
      handleOpenUploadModal={handleOpenUploadModal}
      handleCloseUploadModal={handleCloseUploadModal}
      mvIdsInput={mvIdsInput}
      setMvIdsInput={setMvIdsInput}
      isVerified={isVerified}
      handleCheckMvIds={handleCheckMvIds}
      handleContinue={handleContinue}
      activeTab={activeTab}
      setActiveTab={setActiveTab}
      categories={categories}
      handleBulkMvIdsPaste={handleBulkMvIdsPaste}
      handleResetVerification={handleResetVerification}
      verifiedMvIds={verifiedMvIds}
      invalidMvIds={invalidMvIds}
      handleRemoveInvalidMvId={handleRemoveInvalidMvId}
      duplicateMvIds={duplicateMvIds}
      alreadySelectedMvIds={alreadySelectedMvIds}
      assortmentDetails={assortmentDetails}
      ruleFailures={ruleFailures}
      failureModalOpen={failureModalOpen}
      setFailureModalOpen={setFailureModalOpen}
      successModalOpen={successModalOpen}
      setSuccessModalOpen={setSuccessModalOpen}
      newAssortmentId={newAssortmentId}
      setVerifiedMvIds={setVerifiedMvIds}
      searchLoading={searchLoading} // Pass search loading state
    />
  );
};

function mapStateToProps(props) {
  return {
    props
  };
}

export default connect(mapStateToProps, {
  getDataFromAPI,
})(CohortAssortmentCloneContainer);